/**
 * Learned-map review — variant D (see DYNAMIC_FILE_REVIEW_UI_D_SPEC.md): the
 * file itself as a grid with the mapping painted onto its column headers, a
 * mini-map strip that accounts for every column, and a rail of flags worth
 * seeing before approval. Two remap paths converge on the same validation +
 * dry run: the header inspector (PATCH, deterministic) and the discuss dock
 * (feedback, LLM re-proposal).
 *
 * With no draft pending, the same surface renders the APPROVED version
 * read-only, so a feed's live mapping stays inspectable long after onboarding.
 * Editing an approved map is deliberately absent: it needs a replay of already
 * imported files to avoid splitting history (see DYNAMIC_FILE_INGESTION_SPEC D8).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { AlertTriangle, Check } from 'lucide-react';
import Button from '../../components/ui/Button';
import { integrationsApi } from '../../services/integrations-api';
import type {
  InboundFileDraftBundle,
  InboundFileProfile,
  InboundFileSpec,
  InboundFileVersion,
} from '../../services/integrations-api';
import { handleApiError } from '../../services/api';
import { formatCurrency } from '../../components/dashboard-builder/rendering/formatters';
import InboundFileColumnInspector from './InboundFileColumnInspector';
import InboundFileDiscussDock from './InboundFileDiscussDock';
import {
  buildFlags,
  buildReviewColumns,
  FAMILY_ORDER,
  FAMILY_STYLE,
  slotLabel,
  transformSummary,
  type ReviewColumn,
} from './inboundFileMapping';

const COMPACT_BREAKPOINT = 720;
const PULSE_MS = 1400;
const TOOLTIP_WIDTH = 288;

/** 422 from PATCH carries the validator's `details[]` — show them verbatim. */
function validationDetails(error: unknown): string[] {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { details?: unknown; error?: string } | undefined;
    if (Array.isArray(data?.details) && data.details.length > 0) return data.details.map(String);
    if (data?.error) return [data.error];
  }
  return [handleApiError(error)];
}

const Tile: React.FC<{ value: React.ReactNode; label: React.ReactNode; tone?: 'good' | 'warn' }> = ({
  value,
  label,
  tone,
}) => (
  <div className="rounded-lg border border-border bg-card px-3 py-2.5">
    <div
      className={`text-lg font-bold tabular-nums ${
        tone === 'good' ? 'text-success' : tone === 'warn' ? 'text-warn' : 'text-foreground'
      }`}
    >
      {value}
    </div>
    <div className="mt-0.5 text-xs text-muted-foreground">{label}</div>
  </div>
);

/** Family badge — "slot · Friendly label", with the ★/⏱ markers of record. */
const MappingBadge: React.FC<{ column: ReviewColumn; showLabel?: boolean }> = ({
  column,
  showLabel = true,
}) => (
  <span
    className={`inline-flex max-w-full items-center gap-1 truncate rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold ${
      FAMILY_STYLE[column.family].badge
    }`}
  >
    {column.isTimestamp && <span aria-hidden="true">⏱</span>}
    {column.isMaster && <span className="text-warn" aria-hidden="true">★</span>}
    <span className="truncate">
      {column.slot ? slotLabel(column.slot) : 'skipped'}
      {showLabel && column.binding ? ` · ${column.binding.friendlyLabel}` : ''}
    </span>
  </span>
);

/** Hover/focus card (spec D3) — label, description, type, transform, fill, samples. */
const ColumnTooltip: React.FC<{ column: ReviewColumn; rect: DOMRect }> = ({ column, rect }) => {
  const transform = transformSummary(column.binding);
  const samples = column.profile?.sampleValues ?? [];
  return (
    <div
      role="tooltip"
      style={{
        position: 'fixed',
        width: TOOLTIP_WIDTH,
        left: Math.min(Math.max(8, rect.left), window.innerWidth - TOOLTIP_WIDTH - 8),
        top: rect.bottom + 6,
      }}
      className="pointer-events-none z-40 rounded-lg border border-border bg-card p-2.5 shadow-lg"
    >
      <p className="text-sm font-semibold text-foreground">
        {column.binding?.friendlyLabel ?? column.header}
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {column.binding?.description ?? column.skipReason ?? 'Not imported.'}
      </p>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <MappingBadge column={column} showLabel={false} />
        {column.binding && (
          <span className="font-mono text-[10px] text-muted-foreground">
            {column.binding.dataType}
            {column.binding.unit ? ` · ${column.binding.unit}` : ''}
          </span>
        )}
      </div>
      {transform && <p className="mt-1 text-xs text-muted-foreground">{transform}</p>}
      {column.siblings.length > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">
          combined with {column.siblings.join(', ')}
        </p>
      )}
      {column.profile && (
        <p className="mt-1.5 font-mono text-[10px] text-muted-foreground">
          {Math.round((1 - column.profile.nullRate) * 100)}% filled
          {samples.length > 0 ? ` · ${samples.join(' · ')}` : ''}
        </p>
      )}
    </div>
  );
};

const InboundFileReview: React.FC<{ dataSourceId: string }> = ({ dataSourceId }) => {
  const queryClient = useQueryClient();
  const [dockPrefill, setDockPrefill] = useState<{ text: string; nonce: number } | null>(null);
  // Approving mid-turn would freeze a draft the agent is still changing.
  const [dockBusy, setDockBusy] = useState(false);
  const [hovered, setHovered] = useState<{ index: number; rect: DOMRect } | null>(null);
  const [inspecting, setInspecting] = useState<{ index: number; rect: DOMRect } | null>(null);
  // Read-only mode has no inspector, so a click pins the hover card instead —
  // otherwise touch and keyboard users could never read a column's detail.
  const [pinned, setPinned] = useState<{ index: number; rect: DOMRect } | null>(null);
  const [pulseIndex, setPulseIndex] = useState<number | null>(null);
  const [patchErrors, setPatchErrors] = useState<string[]>([]);
  const [compact, setCompact] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < COMPACT_BREAKPOINT,
  );
  const headerRefs = useRef<Array<HTMLTableCellElement | null>>([]);

  useEffect(() => {
    const onResize = (): void => setCompact(window.innerWidth < COMPACT_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // A pinned card is placed from a rect captured at click time; any scroll
  // (including the grid's own horizontal one) would strand it.
  useEffect(() => {
    if (!pinned) return undefined;
    const drop = (): void => setPinned(null);
    window.addEventListener('scroll', drop, true);
    window.addEventListener('resize', drop);
    return () => {
      window.removeEventListener('scroll', drop, true);
      window.removeEventListener('resize', drop);
    };
  }, [pinned]);

  const draftQuery = useQuery({
    queryKey: ['inbound-file-draft', dataSourceId],
    queryFn: () => integrationsApi.getInboundFileDraft(dataSourceId),
    // A first file may still be processing — keep the empty state fresh.
    refetchInterval: (query) =>
      query.state.data?.draft || query.state.data?.approved ? false : 15_000,
  });

  const bundle: InboundFileDraftBundle | undefined = draftQuery.data;
  /**
   * A draft is what's under review. With none, the approved version renders in
   * the same grid read-only — every edit affordance (inspector, dock, approve)
   * is withheld, because approved rows are immutable and changing a live map
   * without replaying already-imported files would silently split history.
   */
  const version: InboundFileVersion | null = bundle?.draft ?? bundle?.approved ?? null;
  const readOnly = !bundle?.draft;
  const profile: InboundFileProfile | null = version?.profile ?? null;
  const spec: InboundFileSpec | null = version?.spec ?? null;

  const columns = useMemo(
    () => (spec ? buildReviewColumns(spec, profile) : []),
    [spec, profile],
  );
  const flags = useMemo(
    () => (spec ? buildFlags(spec, profile, columns, readOnly) : []),
    [spec, profile, columns, readOnly],
  );

  const approveMutation = useMutation({
    mutationFn: () => integrationsApi.approveInboundFileDraft(dataSourceId),
    onSuccess: (data) => {
      toast.success(
        data.requeuedFiles > 0
          ? `Approved — importing ${data.requeuedFiles} held file${data.requeuedFiles === 1 ? '' : 's'}`
          : 'Approved — future files import automatically',
      );
      void queryClient.invalidateQueries({ queryKey: ['inbound-file-draft', dataSourceId] });
      void queryClient.invalidateQueries({ queryKey: ['inbound-emails', dataSourceId] });
    },
    onError: (error) => toast.error(handleApiError(error) || 'Approval failed'),
  });

  const patchMutation = useMutation({
    mutationFn: (next: InboundFileSpec) =>
      integrationsApi.patchInboundFileDraft(dataSourceId, next),
    onSuccess: () => {
      setInspecting(null);
      setPatchErrors([]);
      toast.success('Mapping updated — dry run re-ran');
      void queryClient.invalidateQueries({ queryKey: ['inbound-file-draft', dataSourceId] });
    },
    onError: (error) => setPatchErrors(validationDetails(error)),
  });

  const scrollToColumn = useCallback((index: number) => {
    headerRefs.current[index]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
    setPulseIndex(index);
  }, []);

  useEffect(() => {
    if (pulseIndex === null) return undefined;
    const timer = window.setTimeout(() => setPulseIndex(null), PULSE_MS);
    return () => window.clearTimeout(timer);
  }, [pulseIndex]);

  const onHeaderClick = (index: number, element: HTMLElement | null): void => {
    const rect = element?.getBoundingClientRect() ?? new DOMRect();
    setHovered(null);
    if (readOnly) {
      setPinned((current) => (current?.index === index ? null : { index, rect }));
      return;
    }
    setPatchErrors([]);
    setInspecting({ index, rect });
  };

  if (draftQuery.isLoading) {
    return <div className="py-8 text-sm text-muted-foreground">Loading mapping…</div>;
  }

  if (!version || !spec) {
    return (
      <div className="py-8">
        <div className="rounded-lg border border-border bg-accent p-5 text-sm text-foreground">
          <p className="mb-1 font-semibold">Waiting for your first file.</p>
          <p className="text-muted-foreground">
            Forward (or auto-forward) a CSV to this source’s address. Zamino will analyze it and
            propose an import mapping here for your approval — nothing is written until you approve.
          </p>
        </div>
      </div>
    );
  }

  const mapped = columns.filter((c) => c.binding).length;
  const skipped = columns.length - mapped;
  const masterColumn = columns.find((c) => c.isMaster) ?? null;
  const masterSum = masterColumn?.profile?.sum;
  const coverage = profile?.coverage ?? null;
  const previewRows = profile?.previewRows ?? [];
  const remainingRows = Math.max(0, (profile?.totalRows ?? 0) - previewRows.length);
  const inspectedColumn = inspecting ? columns[inspecting.index] ?? null : null;
  // Hover still previews while a card is pinned; the pin is what survives the
  // pointer leaving (and is the only path to the detail on touch).
  const tooltipAt = inspecting ? null : hovered ?? pinned;
  const tooltipColumn = tooltipAt ? columns[tooltipAt.index] ?? null : null;
  const approvedOn = version.approvedAt
    ? new Date(version.approvedAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : null;

  return (
    <div className="py-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm">
          <span className="font-semibold text-foreground">{spec.sourceLabel}.</span>{' '}
          <span className="text-muted-foreground">
            Each row becomes one transaction{spec.currency ? ` (amounts in ${spec.currency})` : ''}.
            {readOnly
              ? ' This is the live mapping — matching files import automatically.'
              : ' Nothing is imported until you approve.'}
          </span>
        </p>
        <div className="flex flex-wrap items-center gap-1">
          {FAMILY_ORDER.map((family) => (
            <span
              key={family}
              className={`rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold ${FAMILY_STYLE[family].badge}`}
            >
              {FAMILY_STYLE[family].label}
            </span>
          ))}
        </div>
      </div>

      {/* ── Stats tiles ── */}
      {profile && (
        <div className="mb-2.5 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Tile
            value={
              <>
                {profile.parsedRows}
                <span className="text-xs font-normal text-muted-foreground">
                  {' '}
                  / {profile.totalRows}
                </span>
              </>
            }
            label="rows parse cleanly"
            tone={profile.parsedRows === profile.totalRows ? 'good' : 'warn'}
          />
          <Tile
            value={profile.naturalKeyCollisions}
            label="duplicate keys"
            tone={profile.naturalKeyCollisions > 0 ? 'warn' : 'good'}
          />
          <Tile
            value={
              masterSum === undefined
                ? '—'
                : masterColumn?.binding?.dataType === 'currency' && spec.currency
                  ? formatCurrency(masterSum, spec.currency)
                  : masterSum.toLocaleString()
            }
            label={
              <>
                Σ {masterColumn?.binding?.friendlyLabel ?? 'master metric'}
                {coverage ? ` · ${coverage.minDate} – ${coverage.maxDate}` : ''}
              </>
            }
          />
          <Tile
            value={
              <>
                {mapped}
                <span className="text-xs font-normal text-muted-foreground">
                  {' '}
                  + {skipped} skipped
                </span>
              </>
            }
            label={`of ${columns.length} columns mapped`}
          />
        </div>
      )}

      {/* ── Column mini-map: the whole file accounted for, in file order ── */}
      <div className="mb-2.5 rounded-lg border border-border bg-card px-3 py-2.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            All {columns.length} columns · file order · click to jump
          </p>
          <p className="text-[10px] text-muted-foreground">
            dashed = not imported · ★ master metric · ⏱ time axis · ⚑ worth a look
          </p>
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1" aria-label="Column mini-map">
          {columns.map((column) => (
            <button
              key={column.key}
              type="button"
              onClick={() => scrollToColumn(column.index)}
              onMouseEnter={(e) =>
                setHovered({ index: column.index, rect: e.currentTarget.getBoundingClientRect() })
              }
              onMouseLeave={() => setHovered(null)}
              onFocus={(e) =>
                setHovered({ index: column.index, rect: e.currentTarget.getBoundingClientRect() })
              }
              onBlur={() => setHovered(null)}
              className={`inline-flex max-w-[190px] items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold hover:ring-1 hover:ring-primary focus:outline-none focus:ring-2 focus:ring-primary ${
                FAMILY_STYLE[column.family].badge
              }`}
            >
              {column.isTimestamp && <span aria-hidden="true">⏱</span>}
              {column.isMaster && <span className="text-warn" aria-hidden="true">★</span>}
              <span className="truncate">{column.header}</span>
              {column.worthALook && <span className="text-warn" aria-hidden="true">⚑</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr,240px]">
        {/* ── The file, with the mapping painted on ── */}
        <div className="min-w-0">
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="border-collapse text-xs">
              <thead>
                <tr>
                  <th className="border-b-2 border-r border-border bg-muted px-2 py-1.5 text-left font-mono text-[10px] font-normal text-muted-foreground">
                    #
                  </th>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      ref={(el) => {
                        headerRefs.current[column.index] = el;
                      }}
                      className={`border-b-2 border-r border-border align-bottom ${
                        FAMILY_STYLE[column.family].column
                      } ${pulseIndex === column.index ? 'ring-2 ring-inset ring-primary' : ''}`}
                    >
                      <button
                        type="button"
                        onClick={(e) => onHeaderClick(column.index, e.currentTarget)}
                        onMouseEnter={(e) =>
                          setHovered({
                            index: column.index,
                            rect: e.currentTarget.getBoundingClientRect(),
                          })
                        }
                        onMouseLeave={() => setHovered(null)}
                        onFocus={(e) =>
                          setHovered({
                            index: column.index,
                            rect: e.currentTarget.getBoundingClientRect(),
                          })
                        }
                        onBlur={() => setHovered(null)}
                        aria-expanded={readOnly ? pinned?.index === column.index : undefined}
                        aria-label={
                          readOnly
                            ? `Show how ${column.header} is mapped`
                            : `Edit mapping for ${column.header}`
                        }
                        className="flex w-full min-w-[112px] max-w-[220px] flex-col items-start gap-1 px-2 py-1.5 text-left hover:bg-accent/60 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
                      >
                        <span className="max-w-full truncate font-mono text-[10px] font-bold text-foreground">
                          {column.header}
                          {column.worthALook && <span className="text-warn"> ⚑</span>}
                        </span>
                        <MappingBadge column={column} />
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td className="border-b border-r border-border bg-muted px-2 py-1 text-center font-mono text-[10px] text-muted-foreground">
                      {rowIndex + 1}
                    </td>
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={`whitespace-nowrap border-b border-r border-border px-2 py-1 font-mono text-[10px] tabular-nums ${
                          FAMILY_STYLE[column.family].column
                        }`}
                      >
                        {row[column.index] || '—'}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="px-2 py-1.5 text-center font-mono text-[10px] text-muted-foreground">
                    …
                  </td>
                  <td
                    colSpan={columns.length}
                    className="px-2 py-1.5 text-xs italic text-muted-foreground"
                  >
                    {previewRows.length > 0
                      ? `${remainingRows} more row${remainingRows === 1 ? '' : 's'} · scroll → for the remaining columns`
                      : readOnly
                        ? 'No row sample was stored for this mapping version.'
                        : 'Row preview will appear once the mapping is revised.'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Rail ── */}
        <div className="space-y-2.5">
          {readOnly && (
            <div className="rounded-lg border border-border bg-card px-3 py-2.5">
              <p className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                Live mapping
              </p>
              <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                <Check className="h-3.5 w-3.5 shrink-0 text-success" />
                {version.version}
                {approvedOn ? ` · approved ${approvedOn}` : ''}
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {bundle?.counts.completed ?? 0} file
                {bundle?.counts.completed === 1 ? '' : 's'} imported. If the format ever changes,
                the new shape appears here for review.
              </p>
            </div>
          )}

          {flags.length > 0 && (
            <div className="rounded-lg border border-warn/45 bg-card px-3 py-2.5">
              <p className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-warn">
                {readOnly ? 'Worth knowing' : 'Before you approve'} · {flags.length} flag
                {flags.length === 1 ? '' : 's'}
              </p>
              <ul className="space-y-2 text-xs">
                {flags.map((flag) => (
                  <li key={flag.key}>
                    <p className="flex items-start gap-1 font-semibold text-foreground">
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-warn" />
                      {flag.columnIndex === undefined ? (
                        flag.title
                      ) : (
                        <button
                          type="button"
                          onClick={() => scrollToColumn(flag.columnIndex as number)}
                          className="text-left underline decoration-dotted underline-offset-2 hover:text-primary"
                        >
                          {flag.title}
                        </button>
                      )}
                    </p>
                    <p className="text-muted-foreground">{flag.detail}</p>
                    {flag.kind === 'timezone' && (
                      <Link
                        to="/businesses"
                        className="mt-1 inline-block rounded border border-input px-2 py-0.5 text-[11px] font-medium text-foreground hover:bg-accent"
                      >
                        Set the business timezone
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-lg border border-border bg-card px-3 py-2.5">
            <p className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              Row identity
            </p>
            <div className="flex flex-wrap items-center gap-1">
              {spec.naturalKey.map((slot, i) => (
                <React.Fragment key={slot}>
                  {i > 0 && <span className="text-xs text-muted-foreground">+</span>}
                  <span className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                    {spec.bindings.find((b) => b.slot === slot)?.friendlyLabel ?? slotLabel(slot)}
                  </span>
                </React.Fragment>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Re-sent files update, never duplicate.
            </p>
          </div>

          {!readOnly && (
            <>
              <InboundFileDiscussDock
                dataSourceId={dataSourceId}
                intro={spec.proposalExplanation}
                prefill={dockPrefill}
                disabled={patchMutation.isPending}
                onBusyChange={setDockBusy}
                onMappingChanged={() =>
                  void queryClient.invalidateQueries({
                    queryKey: ['inbound-file-draft', dataSourceId],
                  })
                }
              />

              <div>
                <p className="mb-1.5 text-[11px] text-muted-foreground">
                  Approving imports{' '}
                  <span className="font-semibold text-foreground">
                    {profile?.parsedRows ?? 0} rows
                  </span>
                  {(bundle?.counts.awaitingMap ?? 0) > 1 && (
                    <>
                      {' '}
                      (+ {(bundle?.counts.awaitingMap ?? 0) - 1} more held file
                      {bundle?.counts.awaitingMap === 2 ? '' : 's'})
                    </>
                  )}{' '}
                  and saves this mapping — future files import automatically.
                </p>
                <Button
                  className="w-full"
                  onClick={() => approveMutation.mutate()}
                  disabled={approveMutation.isPending || patchMutation.isPending || dockBusy}
                  isLoading={approveMutation.isPending}
                >
                  <Check className="mr-1.5 h-4 w-4" />
                  Approve &amp; import
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {tooltipColumn && tooltipAt && <ColumnTooltip column={tooltipColumn} rect={tooltipAt.rect} />}

      {!readOnly && inspectedColumn && inspecting && (
        <InboundFileColumnInspector
          key={`${inspectedColumn.key}:${version.updatedAt}`}
          column={inspectedColumn}
          spec={spec}
          anchor={inspecting.rect}
          compact={compact}
          isSaving={patchMutation.isPending}
          errors={patchErrors}
          onApply={(next) => patchMutation.mutate(next)}
          onAskZamino={() => {
            setInspecting(null);
            setDockPrefill({
              text: `About the column "${inspectedColumn.header}": `,
              nonce: Date.now(),
            });
          }}
          onClose={() => {
            setInspecting(null);
            setPatchErrors([]);
          }}
        />
      )}
    </div>
  );
};

export default InboundFileReview;
